/**
 * Interview API
 * Handles interview-related API endpoints
 */

import { executeRequest } from './hooks/useApiQuery';
import { BACKEND_URL, API_VERSION } from '@/config/api';
import type {
  SSEChunkData,
  SSECompleteData,
  SSEConnectedData,
  SSEErrorData,
  SSEMetadataData,
  SSEToolCallData,
  SSEToolResultData,
} from '@/types/sse.types';

const INTERVIEW_ENDPOINT = `${BACKEND_URL}${API_VERSION}/interview`;

export type InterviewStatus = 'not_started' | 'in_progress' | 'completed';
export type InterviewInitType = 'start' | 'resume';

/**
 * Interview Phase Types
 * Phase 1: Problem Understanding (Q&A between AI and candidate)
 * Phase 2: Coding (candidate writes code, AI is silent)
 * Phase 3: Code Review (Q&A about the code, then interview ends)
 */
export type InterviewPhase = 1 | 2 | 3;

export interface ProblemTestCase {
  input: string;
  expectedOutput: string;
  description: string;
  fullExecutableCode?: string;
}

export interface ProblemLanguageVariant {
  starter_code: string;
  test_cases: ProblemTestCase[];
  generatedAt?: string;
}

export interface ProblemInfo {
  problem_title: string;
  problem_description: string;
  problem_level?: string;
  starter_code?: string;
  initial_problem_language?: string;
  active_coding_language?: string;
  test_cases?: ProblemTestCase[];
  languages?: Record<string, ProblemLanguageVariant>;
}

export interface SubmittedCodeInfo {
  code: string;
  language: string;
  submittedAt?: string;
}

export interface InterviewInfo {
  job_title?: string;
  job_description?: string;
  experience_level?: string;
  candidate_resume?: string;
  problem?: ProblemInfo;
  currentActivePhase?: InterviewPhase;
  codingPhaseElapsedSeconds?: number;
  submittedCode?: SubmittedCodeInfo;
}

/**
 * Phase transition metadata sent via SSE when AI calls move_to_next_phase tool
 */
export interface PhaseTransitionMetadata {
  fromPhase: InterviewPhase;
  toPhase: InterviewPhase;
  reason: string;
}

export interface InterviewData {
  interview_status: InterviewStatus;
  user_name?: string;
  user_email?: string;
  type?: string;
  duration?: number;
  version?: string;
  interview_info?: InterviewInfo;
  [key: string]: unknown;
}

export interface PrepareInterviewResponse {
  interview_id: string;
  systemPromptStatus: 'preparing' | 'completed';
  interview?: InterviewData;
  resumeFromTimestamp?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Prepares an interview by calling the /prepare endpoint
 * @param interview_id - The interview ID to prepare
 * @returns Promise resolving to the prepare response with status
 */
export async function prepareInterview(interview_id: string): Promise<PrepareInterviewResponse> {
  const response = await executeRequest<ApiResponse<PrepareInterviewResponse>>({
    url: '/interview/prepare',
    method: 'POST',
    body: {
      interview_id,
    },
  });
  
  // Extract data from the API response wrapper
  return response.data;
}

export interface GetInterviewDetailsResponse {
  interview: InterviewData & { _id: string };
}

/**
 * Fetches interview details by ID
 * @param interview_id - The interview ID to fetch
 * @returns Promise resolving to the interview details
 */
export async function getInterviewDetails(interview_id: string): Promise<GetInterviewDetailsResponse> {
  const response = await executeRequest<ApiResponse<GetInterviewDetailsResponse>>({
    url: `/interview/${interview_id}`,
    method: 'GET',
  });
  
  return response.data;
}

export interface InterviewStreamPayload {
  interview_id: string;
  user_message?: string;
  timestamp?: string;
  interview_init_type?: InterviewInitType;
  candidate_name?: string;
}

export interface InterviewStreamHandlers {
  onConnected?: (data: SSEConnectedData) => void;
  onChunk?: (data: SSEChunkData) => void;
  onComplete?: (data: SSECompleteData) => void;
  onError?: (data: SSEErrorData) => void;
  onMetadata?: (data: SSEMetadataData) => void;
  onToolCall?: (data: SSEToolCallData) => void;
  onToolResult?: (data: SSEToolResultData) => void;
  onAbort?: () => void;
}

export interface InterviewStreamController {
  abort: () => void;
}

const parseSSEEvents = (
  rawChunk: string,
  dispatch: (event: string, data: unknown, rawData: string) => void,
  bufferRef: { current: string },
) => {
  bufferRef.current += rawChunk;
  const events = bufferRef.current.split('\n\n');
  bufferRef.current = events.pop() ?? '';

  for (const rawEvent of events) {
    if (!rawEvent.trim()) continue;

    const lines = rawEvent.split('\n');
    let eventType = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.replace('event:', '').trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.replace('data:', '').trim());
      }
    }

    const dataText = dataLines.join('\n');
    let parsedData: unknown = dataText;

    if (dataText) {
      try {
        parsedData = JSON.parse(dataText);
      } catch {
        // ignore parse failures
      }
    }

    dispatch(eventType, parsedData, dataText);
  }
};

/**
 * Stream interview responses over SSE using POST /interview
 */
export async function streamInterview(
  payload: InterviewStreamPayload,
  handlers: InterviewStreamHandlers = {},
): Promise<InterviewStreamController> {
  const abortController = new AbortController();
  let abortNotified = false;
  const headers = new Headers({
    'Content-Type': 'application/json',
  });

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(INTERVIEW_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: abortController.signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `Interview stream failed (${response.status} ${response.statusText})${errorBody ? `: ${errorBody}` : ''}`,
    );
  }

  if (!response.body) {
    throw new Error('Interview stream has no response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const bufferRef = { current: '' };

  const dispatchEvent = (eventType: string, data: unknown, rawData: string) => {
    switch (eventType) {
      case 'connected':
        handlers.onConnected?.(data as SSEConnectedData);
        break;
      case 'chunk':
        handlers.onChunk?.(data as SSEChunkData);
        break;
      case 'complete':
        handlers.onComplete?.(data as SSECompleteData);
        break;
      case 'error':
        handlers.onError?.(data as SSEErrorData);
        break;
      case 'metadata':
        handlers.onMetadata?.(data as SSEMetadataData);
        break;
      case 'tool-call':
        handlers.onToolCall?.(data as SSEToolCallData);
        break;
      case 'tool-result':
        handlers.onToolResult?.(data as SSEToolResultData);
        break;
      default:
        // ignore unhandled SSE event types
    }
  };

  const pump = async () => {
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Flush any remaining buffered text
          const remaining = decoder.decode();
          if (remaining) {
            parseSSEEvents(remaining, dispatchEvent, bufferRef);
          }
          if (bufferRef.current.trim()) {
            parseSSEEvents('\n\n', dispatchEvent, bufferRef);
          }
          break;
        }

        const chunkText = decoder.decode(value, { stream: true });
        parseSSEEvents(chunkText, dispatchEvent, bufferRef);
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        if (!abortNotified) {
          abortNotified = true;
          handlers.onAbort?.();
        }
        return;
      }
      handlers.onError?.({
        error: 'client_error',
        message: error instanceof Error ? error.message : 'Unknown stream error',
      });
    }
  };

  void pump();

  return {
    abort: () => {
      if (abortNotified) return;
      abortNotified = true;
      abortController.abort();
      handlers.onAbort?.();
    },
  };
}

/**
 * Test result structure for code submission
 */
export interface SubmittedTestResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  status: 'pass' | 'fail' | 'error';
}

/**
 * Updates the coding phase elapsed time for an interview.
 * Called periodically (every 60s) during Phase 2 to persist timer state.
 * @param interview_id - The interview ID
 * @param elapsedSeconds - The elapsed time in seconds
 */
export async function updateCodingPhaseTime(
  interview_id: string,
  elapsedSeconds: number,
): Promise<void> {
  await executeRequest<ApiResponse<{ interview_id: string; elapsedSeconds: number }>>({
    url: `/interview/${interview_id}/coding-time`,
    method: 'PATCH',
    body: { elapsedSeconds },
  });
}

/**
 * Submits the candidate's code and transitions from Phase 2 to Phase 3.
 * @param interview_id - The interview ID
 * @param code - The submitted code
 * @param language - The programming language
 * @param testResults - Array of test case results
 */
export async function submitCode(
  interview_id: string,
  code: string,
  language: string,
  testResults: SubmittedTestResult[],
): Promise<void> {
  await executeRequest<ApiResponse<{ interview_id: string; phase: number }>>({
    url: `/interview/${interview_id}/submit-code`,
    method: 'POST',
    body: { code, language, testResults },
  });
}

export interface SwitchProblemLanguageResponse {
  problem: ProblemInfo;
  language: string;
  variant: ProblemLanguageVariant;
}

/**
 * Switches the coding language and fetches starter code + test cases for the target language.
 * @param interview_id - The interview ID
 * @param language - The programming language
 */
export async function switchProblemLanguage(
  interview_id: string,
  language: string,
): Promise<SwitchProblemLanguageResponse> {
  const response = await executeRequest<ApiResponse<SwitchProblemLanguageResponse>>({
    url: `/interview/${interview_id}/problem-language`,
    method: 'POST',
    body: { language },
  });

  return response.data;
}
